// Tier 3 — Market budget wiring.
// Pure functions. Consumes Tier 1 affordability output (max_affordable_property).
// No React, no state. Deterministic for the same inputs.

// Budget classification thresholds (relative to affordable budget).
// Within   : propertyCost <= budget
// Stretch  : propertyCost <= budget * (1 + STRETCH_PCT/100)
// Beyond   : otherwise
const STRETCH_PCT = 10;

function classify(propertyCost, budget) {
    if (budget <= 0) return { status: "Beyond", gap: propertyCost };
    if (propertyCost <= budget) {
        return { status: "Within", gap: propertyCost - budget }; // negative = headroom
    }
    if (propertyCost <= budget * (1 + STRETCH_PCT / 100)) {
        return { status: "Stretch", gap: propertyCost - budget };
    }
    return { status: "Beyond", gap: propertyCost - budget };
}

// Per-market Growth Score — derived deterministically from structured fields.
// (Replaces the legacy hand-fed `score`. Reflects growth potential only,
// NOT livability or safety.)
//   • CAGR clamp 5–20% → 0–40 pts
//   • Yield clamp 2–5% → 0–20 pts
//   • Risk:       Low=20 · Mid=10 · High=0
//   • Confidence: High=20 · Medium=10 · Low=0
function computeGrowthScore(m) {
    const growthPts = Math.max(0, Math.min(1, (m.growth - 5) / 15)) * 40;
    const yieldPts = Math.max(0, Math.min(1, (m.rentYield - 2) / 3)) * 20;
    const riskPts = m.investmentRisk === "Low" ? 20 : m.investmentRisk === "Mid" ? 10 : 0;
    const confPts = m.confidence === "High" ? 20 : m.confidence === "Medium" ? 10 : 0;
    return Math.round(growthPts + yieldPts + riskPts + confPts);
}

// ── Tier 3 · Market composite ──────────────────────────────────────
export function computeMarketOutputs(inputs) {
    const {
        markets = [],
        sba = 1500,
        wealthYear = 10,
        budget = 0,
    } = inputs || {};

    // M1 · per-market decoration
    const decorated = markets.map((m) => {
        // Property cost uses the Mid band by default. `m.psf` is kept as a
        // back-compat mirror of psfBands.mid; prefer the structured field here.
        const psfForCost = m.psfBands?.mid ?? m.psf;
        const propertyCost = psfForCost * sba;
        const annualRental = propertyCost * (m.rentYield / 100);
        const futureValue = propertyCost * Math.pow(1 + m.growth / 100, wealthYear);
        const wealthCreated = futureValue - propertyCost;
        const { status, gap } = classify(propertyCost, budget);
        // Derive Growth Score if the row doesn't already carry one (it shouldn't).
        const growth_score = computeGrowthScore(m);
        return {
            ...m,
            score: growth_score,        // back-compat: legacy consumers read `m.score`
            growth_score,
            propertyCost,
            annualRental,
            futureValue,
            wealthCreated,
            status,
            budget_gap: gap,
        };
    });

    // M2 · aggregate counts
    const within = decorated.filter((m) => m.status === "Within");
    const stretch = decorated.filter((m) => m.status === "Stretch");
    const beyond = decorated.filter((m) => m.status === "Beyond");
    const total = decorated.length || 1;
    const within_count = within.length;
    const stretch_count = stretch.length;
    const beyond_count = beyond.length;
    const within_pct = (within_count / total) * 100;
    const reachable_pct = ((within_count + stretch_count) / total) * 100;

    // M3 · best within-budget market (highest score; ties broken by growth)
    const best_within = within
        .slice()
        .sort((a, b) => (b.score - a.score) || (b.growth - a.growth))[0] || null;

    // M4 · cheapest beyond-budget gap (smallest overspend to enter market)
    const cheapest_stretch_or_beyond = decorated
        .filter((m) => m.status !== "Within")
        .slice()
        .sort((a, b) => a.budget_gap - b.budget_gap)[0] || null;

    // M5 · Market sub-score (S_mkt). 40 base + tiered, cap 100.
    //   Drives the market arm of the composite HomeIQ score. Logic:
    //   • Very strong : ≥ 8 within OR within_pct ≥ 50%      → +50
    //   • Strong      : ≥ 5 within OR within_pct ≥ 30%      → +40
    //   • Workable    : ≥ 3 within                         → +30
    //   • Tight       : ≥ 1 within                         → +20
    //   • Beyond      : 0 within but ≥ 3 stretch          → +10
    //   • Out of reach: nothing within reach              → +0
    //   Plus +10 if a within-budget market also has CAGR ≥ 6.5%.
    let tier = 0;
    if (within_count >= 8 || within_pct >= 50) tier = 50;
    else if (within_count >= 5 || within_pct >= 30) tier = 40;
    else if (within_count >= 3) tier = 30;
    else if (within_count >= 1) tier = 20;
    else if (stretch_count >= 3) tier = 10;

    const hasGrowthWithin = within.some((m) => m.growth >= 6.5);
    const market_score = Math.min(100, 40 + tier + (hasGrowthWithin ? 10 : 0));

    return {
        // raw echoes
        sba, wealthYear, budget,
        // M1 — decorated dataset
        decorated,
        // M2 — counts
        within_count,
        stretch_count,
        beyond_count,
        within_pct,
        reachable_pct,
        // M3/M4 — picks
        best_within,
        cheapest_stretch_or_beyond,
        // M5 — score
        market_score,
        // Threshold echo (so UI can render legend dynamically)
        stretch_pct: STRETCH_PCT,
    };
}
